import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DublettenTreffer, KundeStamm, Unternehmen,
              FieldSuggestion, LogoCandidate, PartnerRole } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, darfAbbrechen, useBeforeUnload, useFormularZustand, useBestaetigung,
         useSofortPopup } from "@kundeninfo/ui";
import { Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../router.js";
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "../components/meHuellen.js";
import { Ortszeile } from "../components/Ortszeile.js";
import { useLaender } from "../laender.js";
import { LOGO_TEXT, PARTNER_TEXT, SOURCE_LABEL } from "./partners/texts.js";
import { LogoChoice, websiteForLookup } from "./partners/LogoChoice.js";
import { RoleChoiceField, hasRole, roleChoiceFrom, rolesOf } from "./partners/RoleChoice.js";
import type { RoleChoice } from "./partners/RoleChoice.js";
import { ROLLEN } from "./partners/rollenFilter.js";
import "./KundeAnlegen.css";

/** Nur die Klientenmethoden, die diese Maske wirklich braucht — so lässt sie sich im Test
 *  mit wenigen vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen. lookupEnrichment und
 *  uploadCustomerLogo kamen mit der Anreicherung dazu (Aufgabe 7). */
export type AnlegenClient =
  Pick<ApiClient, "findDuplicates" | "createCustomer"
    | "lookupEnrichment" | "uploadCustomerLogo"
    // „Rolle Kunde hinzufügen“ am Dublettentreffer (Lieferanten Strang A): Beziehung lesen, Rollen setzen bzw. anlegen.
    | "getCustomer" | "setRelationRoles" | "addRelation"
    // Die Ortszeile (Spec „Adresszeile", R17): Länderkürzel und PLZ-Verzeichnis.
    | "listLaender" | "listPlzOrte">;

export type KundeAnlegenProps = {
  client: AnlegenClient;
  /** Die sichtbaren Unternehmen (sitzung.companies) — für die Kürzel-Chips der Treffer, den
   *  Namen im Zwischentitel und das Ziel von „Zu <Kürzel> wechseln". */
  companies: Unternehmen[];
  /** Das aktive Unternehmen — hier entsteht der Kunde (E361). */
  arbeitsunternehmenId: string | null;
  /** Nach dem Anlegen oder bei „Zur Kundenakte" am Treffer: die Akte dieses Kunden öffnen. */
  onFertig(id: string): void;
  /** „Zu <Kürzel> wechseln" am Dublettentreffer (E361, R20): App wechselt das aktive Unternehmen
   *  und öffnet nach dem Neuladen die Akte des Treffers im Reiter Stammdaten. `ungefragt`: Die Maske
   *  trägt nur den Suchnamen, es geht nichts verloren — App fragt dann keinen Wächter und gibt den
   *  Browser-Schutz erst unmittelbar vor dem Neuladen frei. */
  onWechseln(companyId: string, kundeId: string, o: { ungefragt: boolean }): void;
  /**
   * Kreuz, Escape und „Abbrechen“ (Entscheid 524, Hausregel „Modal-Bearbeitung“): Der Aufrufer hängt das Panel aus. Mit
   * Eingaben ist die Rückfrage („Änderungen verwerfen?“) vorher beantwortet, der Verlassen-Wächter abgemeldet — die
   * Navigation danach fragt kein zweites Mal.
   */
  onAbbrechen(): void;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
  /** Die aktiven Rollen-Chips der Liste — Vorgabe der Rollenwahl (C9); ohne Angabe alle (= Bestandsvorgabe). */
  rollenVorgabe?: readonly PartnerRole[];
};

/** Ab so vielen Zeichen sucht die Maske nach bestehenden Firmen. */
const MIN_SUCHLAENGE = 3;
/** Entprellung der Dublettensuche — ohne sie eine Abfrage je Tastendruck. */
const SUCH_VERZOEGERUNG_MS = 300;
/** Entprellung des Anreicherungs-Lookups — dasselbe Idiom wie die Dublettensuche, nur
 *  etwas länger, weil dahinter fremde Quellen (VIES, Impressum, Webseite) hängen. */
const ANREICHERUNG_VERZOEGERUNG_MS = 500;

/** Die Rollen eines Dublettentreffers in Worten (Kennzeichen-Schlüssel, P12). */
const ROLLEN_TEXT: Record<PartnerRole, string> = {
  CUSTOMER: PARTNER_TEXT.roleCustomer, PROSPECT: PARTNER_TEXT.roleProspect, SUPPLIER: PARTNER_TEXT.roleSupplier,
};

/** Die Formularwerte des Firmenblocks — überall Zeichenketten; leer wird erst beim
 *  Absenden zu null (wie in KundeStammdaten.tsx). */
type Entwurf = {
  companyName: string; street: string; houseNumber: string; postalCode: string;
  city: string;
  phone: string; email: string; vatId: string; industry: string; website: string;
  country: string;
};

const LEERER_ENTWURF: Entwurf = {
  companyName: "", street: "", houseNumber: "", postalCode: "", city: "",
  phone: "", email: "", vatId: "", industry: "", website: "", country: "DE",
};

// Die Webadresse steht zwischen E-Mail und Branche: Sie gehört zu den Kontaktwegen, und
// sie ist der Ausgangspunkt der späteren Anreicherung (Impressum, Logo). Ohne Schema
// getippt („example.de") ergänzt der Server https:// (normalizeWebsite in domain) — die
// Maske schreibt bewusst nichts um, sonst stünden zwei Stellen für dieselbe Regel.
const FELDER: { name: keyof Entwurf; label: string; typ?: "email" | "tel" }[] = [
  { name: "street", label: "Straße" },
  { name: "houseNumber", label: "Hausnummer" },
  { name: "postalCode", label: "PLZ" },
  { name: "city", label: "Ort" },
  { name: "phone", label: "Telefon", typ: "tel" },
  { name: "email", label: "E-Mail", typ: "email" },
  { name: "website", label: "Webseite" },
  { name: "vatId", label: "USt-IdNr." },
  { name: "industry", label: "Branche" },
];

/** Alle Feldnamen, die eine 422 nennen kann, auf die Eingaben dieser Maske abgebildet; `status` und
 *  `supplierStatus` (ROLLE_FEHLT) stehen an der Rollenwahl. */
type EingabeName = keyof Entwurf | "customerNumber" | "status" | "supplierStatus" | "customerSince";

const EINGABE_NAMEN: EingabeName[] = [
  "companyName", ...FELDER.map((f) => f.name), "country",
  "customerNumber", "status", "supplierStatus", "customerSince",
];

function oderNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert.trim();
}

function stammAus(entwurf: Entwurf): KundeStamm {
  return {
    companyName: entwurf.companyName,
    street: oderNull(entwurf.street),
    houseNumber: oderNull(entwurf.houseNumber),
    postalCode: oderNull(entwurf.postalCode),
    city: oderNull(entwurf.city),
    phone: oderNull(entwurf.phone),
    email: oderNull(entwurf.email),
    vatId: oderNull(entwurf.vatId),
    industry: oderNull(entwurf.industry),
    website: oderNull(entwurf.website),
    // Das Land kommt seit der Ortszeile aus der Maske (Spec „Adresszeile", E400) — Vorgabe DE.
    country: entwurf.country,
  };
}

function ortVon(t: DublettenTreffer): string {
  return [t.postalCode, t.city]
    .filter((teil): teil is string => teil !== null && teil !== "")
    .join(" ");
}

/**
 * Die Maske „Neuer Geschäftspartner“ — seit Entscheid 524 (Dirk 30.10.2026) ein Modalpanel (`Arbeitsdialog`, rechts
 * angeschlagen, Muster `TicketAnlegen`) über der aktuellen Ansicht, nicht mehr Inhalt der Fokusspalte; die Adresse
 * /kunden/neu öffnet es als Direktaufruf. Ihre Reihenfolge ist Absicht: ZUERST der Firmenname —
 * ab drei Zeichen sucht die Maske (entprellt) über ALLE Unternehmen nach bestehenden
 * Firmen und lenkt bei einem Treffer in einem anderen Unternehmen zum Wechsel dorthin
 * (Mandantentrennung E361, R20) statt auf eine zweite Firmenzeile. Verboten wird nichts:
 * Wer sicher ist, dass es eine andere Firma ist, legt trotzdem an (siehe Spezifikation
 * „Einen Kunden anlegen"). Der Kunde entsteht immer im aktiven Unternehmen.
 *
 * Das Nummernfeld bleibt leer und trägt nur den Hinweis, dass die Nummer beim Speichern
 * vergeben wird — KEIN Vorschlag beim Öffnen: Zwei gleichzeitig offene Formulare
 * bekämen denselben, und jedes abgebrochene Formular verbrännte eine Nummer. Eine
 * bestehende Nummer (Portal-Mandant, später DATEV-Debitor) wird von Hand eingetragen.
 */
export function KundeAnlegen(p: KundeAnlegenProps): ReactElement {
  const [entwurf, setEntwurf] = useState<Entwurf>(LEERER_ENTWURF);
  const laender = useLaender(p.client.listLaender);
  const [nummer, setNummer] = useState("");
  const [startRollen] = useState<RoleChoice>(() => roleChoiceFrom(p.rollenVorgabe ?? ROLLEN));
  const [rollen, setRollen] = useState<RoleChoice>(startRollen);
  const [seit, setSeit] = useState("");
  /** Nummer und „Kunde seit“ gehören zur Kundenrolle — ohne sie stehen die Felder nicht da und gehen nicht mit. */
  const mitKundenrolle = rollen.customer !== null;
  const rolleGewaehlt = hasRole(rollen);

  const [treffer, setTreffer] = useState<DublettenTreffer[]>([]);
  /** Nur noch der Hinweis „angelegt, aber das Logo nicht“ — Befunde des Anlegens selbst
   *  führt `formular.meldung`. */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Schreibweg, den `formular.aktion()` gleich ausführt — gesetzt beim Klick, damit er
   *  den Entwurf DIESES Zeichnens liest. */
  const auftrag = useRef<() => Promise<{ id: string; logoFehler: string | null }>>(
    async () => ({ id: "", logoFehler: null }));

  // Anreicherung (Aufgabe 7): Vorschläge und Logo-Kandidaten aus dem Lookup. Der Dienst
  // schreibt nie selbst — diese Werte werden Feld für Feld ausdrücklich übernommen.
  const [vorschlaege, setVorschlaege] = useState<FieldSuggestion[]>([]);
  const [logoKandidaten, setLogoKandidaten] = useState<LogoCandidate[]>([]);
  /** Die im Logo-Dialog bestätigte URL — erst NACH dem Anlegen an die Logo-Route gereicht. */
  const [gewaehltesLogo, setGewaehltesLogo] = useState<string | null>(null);
  /** Gesetzt, wenn der Kunde ANGELEGT ist, das gewählte Logo aber scheiterte: Dann wird
   *  NICHT automatisch navigiert (sonst sähe niemand die Meldung), sondern der Bearbeiter
   *  springt selbst mit „Zur Kundenakte" in die Akte (fertig(id)). Kein Rollback. */
  const [angelegtId, setAngelegtId] = useState<string | null>(null);

  // Die Dublettensuche: entprellt, und der laufende Timer wird bei jeder weiteren
  // Eingabe wie beim Verlassen der Maske aufgeräumt (die Effekt-Aufräumfunktion läuft
  // in beiden Fällen). `abgebrochen` fängt zusätzlich eine schon abgeschickte Antwort
  // ab, die erst nach dem Verlassen einträfe.
  const suchName = entwurf.companyName.trim();
  const suche = p.client.findDuplicates;
  useEffect(() => {
    if (suchName.length < MIN_SUCHLAENGE) { setTreffer([]); return; }
    let abgebrochen = false;
    const timer = setTimeout(() => {
      suche(suchName)
        .then((antwort) => { if (!abgebrochen) setTreffer(antwort.items); })
        // Die Suche ist Lenkung, kein Pflichtweg: Scheitert sie (Netz weg), bleibt die
        // Maske still bedienbar, statt das Anlegen mit einer Fehlermeldung zu verstellen.
        .catch(() => {});
    }, SUCH_VERZOEGERUNG_MS);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [suchName, suche]);

  // Der Anreicherungs-Lookup — bewusst nach demselben Muster wie die Dublettensuche oben:
  // entprellt, mit `abgebrochen`-Flag gegen eine veraltete Antwort und `.catch(()=>{})`,
  // damit eine kranke Quelle die Maske NIE verstellt und das Anlegen NIE blockiert.
  // Auslöser ist die USt-IdNr ODER die Webseite (der Firmenname allein löst nicht aus —
  // keine Quelle fängt damit etwas an); ist ein Firmenname ≥ 3 Zeichen da, geht er als
  // zusätzlicher Hinweis mit.
  const vatTrim = entwurf.vatId.trim();
  // Dieselbe Regel wie der Knopf in den Stammdaten (Entscheid 673, Fixrunde 1 H3): ohne Schema mit https://, Text
  // ohne Domain fragt nicht an. Das Feld selbst bleibt, wie getippt.
  const webAdresse = websiteForLookup(entwurf.website);
  const nameTrim = entwurf.companyName.trim();
  const anreichern = p.client.lookupEnrichment;
  useEffect(() => {
    if (vatTrim === "" && webAdresse === null) { setVorschlaege([]); setLogoKandidaten([]); return; }
    let abgebrochen = false;
    const timer = setTimeout(() => {
      const hinweis: { vatId?: string; companyName?: string; website?: string } = {};
      if (vatTrim !== "") hinweis.vatId = vatTrim;
      if (webAdresse !== null) hinweis.website = webAdresse;
      if (nameTrim.length >= MIN_SUCHLAENGE) hinweis.companyName = nameTrim;
      anreichern(hinweis)
        .then((antwort) => {
          if (abgebrochen) return;
          setVorschlaege(antwort.suggestions);
          setLogoKandidaten(antwort.logoCandidates);
        })
        .catch(() => {});
    }, ANREICHERUNG_VERZOEGERUNG_MS);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [vatTrim, webAdresse, nameTrim, anreichern]);

  // Die Kacheln gelten nur für ihre Webseite — wie in den Stammdaten (Entscheid 673, Fixrunde 1 H1, „durchgängig
  // einheitlich“): Ändert sie sich, fallen die alten Kacheln und eine schon gewählte Kachel sofort weg, nicht erst mit
  // der Antwort zur neuen Adresse. Vor dem Zeichnen (Layout-Effekt), damit nichts Altes aufblitzt.
  const logoAdresse = useRef(webAdresse);
  useLayoutEffect(() => {
    if (logoAdresse.current === webAdresse) return;
    logoAdresse.current = webAdresse;
    setLogoKandidaten([]);
    setGewaehltesLogo(null);
  }, [webAdresse]);

  // Verlassen-Wächter: registriert, sobald irgendeine Eingabe von der leeren Maske
  // abweicht — eine halb ausgefüllte Anlage-Maske ist genau der Fall, für den er gebaut
  // wurde. `abmeldenRef` erlaubt dem Erfolgsweg, den Wächter VOR dem eigenen Sprung in
  // die Akte abzumelden — die Effekt-Aufräumung liefe erst nach der Navigation.
  // `weitereEingaben`: alles außer dem Firmennamen — der allein ist der Suchbegriff der
  // Dublettenlenkung, an ihm geht beim Sprung zu einem Treffer nichts verloren.
  const weitereEingaben = FELDER.some((f) => entwurf[f.name] !== "") || entwurf.country !== "DE"
    || nummer !== "" || seit !== ""
    || rollen.customer !== startRollen.customer || rollen.supplier !== startRollen.supplier;
  const geaendert = entwurf.companyName !== "" || weitereEingaben;
  const abmeldenRef = useRef<(() => void) | null>(null);
  const { bestaetigen } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!geaendert) return;
    const abmelden = registriere(() => bestaetigen({
      titel: "Seite verlassen?",
      text: "Es gibt ungespeicherte Änderungen. Sie gehen verloren.",
      bestaetigenText: "Verlassen",
      gefaehrlich: true,
    }));
    abmeldenRef.current = abmelden;
    return () => { abmeldenRef.current = null; abmelden(); };
  }, [geaendert, registriere]);

  // …und derselbe Stand für das ECHTE Verlassen (Tab zu, neu laden, App-Wechsel), das
  // der Wächter oben nicht sieht (2a-Kleinpunkt).
  useBeforeUnload(geaendert);

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3): Während des Anlegens sind alle Felder
   * gesperrt (readOnly + aria-disabled, der Fokus bleibt) und der Knopf steht; eine 422
   * hängt am genannten Feld — auch an der Auswahl Status — und der Fokus
   * springt dorthin; ein Befund zu einem Feld, das diese Maske nicht zeigt, steht in der
   * Meldung (nie unsichtbar). Anlegen kennt keine Version, also keine 409.
   */
  const formular = useFormularZustand({
    speichern: () => auftrag.current(),
    neuLaden: async () => { /* keine 409 beim Anlegen */ },
    bekannteFelder: EINGABE_NAMEN,
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<EingabeName, string>>;

  /** Die Beziehungswerte (rechte Hälfte der Maske): die Rollen (P2) und — nur mit Kundenrolle — Nummer und Datum. */
  function beziehungsWerte(): { customerNumber: string | null; status: "ACTIVE" | "PROSPECT" | null;
    supplierStatus: "ACTIVE" | null; customerSince: string | null } {
    return { customerNumber: mitKundenrolle ? oderNull(nummer) : null, ...rolesOf(rollen),
      customerSince: mitKundenrolle ? oderNull(seit) : null };
  }

  /** Der Erfolgsweg nach dem Anlegen: Wächter abmelden, dann in die Akte springen. */
  function fertig(id: string): void {
    abmeldenRef.current?.();
    p.onFertig(id);
  }

  /** Der Sprung in die Akte eines Dublettentreffers: Mit dem Suchnamen allein geht nichts verloren — dann
   *  ohne Frage (der Wächter meldet sich ab); sonst fragt er bei der Navigation. */
  function zurAkteDesTreffers(id: string): void {
    if (!weitereEingaben) abmeldenRef.current?.();
    p.onFertig(id);
  }

  /**
   * Der eine Weg hinaus neben dem Anlegen (Kreuz, Escape, „Abbrechen“): ohne Eingaben sofort, mit Eingaben nach der
   * gestapelten Rückfrage. Der Verlassen-Wächter wird vorher abgemeldet, sonst fragte die Navigation des Aufrufers
   * (Direktaufruf → Kundenliste) ein zweites Mal. Steht der Partner schon (nur das Logo scheiterte), geht nichts
   * verloren: Der Weg hinaus führt in seine Akte, wie der Knopf „Zur Kundenakte“. Solange das Anlegen läuft, kommt
   * nichts hierher: Kreuz und Escape sperrt `abbrechenGesperrt` am Dialog, den Knopf `deaktiviert` — ein einziger
   * Riegel je Weg, keine zweite Abfrage in dieser Funktion.
   */
  async function abbrechen(): Promise<void> {
    if (angelegtId !== null) { fertig(angelegtId); return; }
    if (!(await darfAbbrechen(geaendert))) return;
    abmeldenRef.current?.();
    p.onAbbrechen();
  }

  async function anlegen(): Promise<void> {
    if (!rolleGewaehlt) return;
    setMeldung(null);
    auftrag.current = async () => {
      // Kein companyId: Der Server nimmt das aktive Unternehmen der Anfrage (R7).
      const antwort = await p.client.createCustomer(
        { customer: stammAus(entwurf), ...beziehungsWerte() });
      // Reihenfolge Anlegen → Logo: die Logo-Route braucht die frisch vergebene Kunden-Id.
      // Scheitert das Logo, BLEIBT der Kunde bestehen (kein Rollback) — und das ist KEIN
      // Fehler des Anlegens: Er geht als Ergebnis zurück, nicht als Ausnahme.
      if (gewaehltesLogo !== null) {
        try {
          await p.client.uploadCustomerLogo(antwort.id, gewaehltesLogo);
        } catch (logoFehler) {
          const grund = logoFehler instanceof ApiError ? logoFehler.message : LOGO_TEXT.unknownError;
          return { id: antwort.id, logoFehler: grund };
        }
      }
      return { id: antwort.id, logoFehler: null };
    };
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined) return; // Befund am Feld oder in der Meldung
    if (ergebnis.logoFehler !== null) {
      // NICHT automatisch navigieren — die Navigation hängt die Maske aus, und die Meldung
      // wäre nie zu sehen (Abschlussfund W1). Stattdessen bleibt die Maske stehen, zeigt
      // die Meldung und einen ausdrücklichen Knopf „Zur Kundenakte".
      setMeldung("Der Kunde wurde angelegt, das Logo konnte aber nicht übernommen werden: "
        + ergebnis.logoFehler);
      setAngelegtId(ergebnis.id);
      return;
    }
    fertig(ergebnis.id);
  }

  /** Das aktive Unternehmen — hier entsteht der Kunde; an ihm misst sich, ob ein
   *  Dubletten-Treffer schon „hier" Kunde ist (siehe Trefferliste). */
  const aktiv = p.companies.find((c) => c.id === p.arbeitsunternehmenId);

  /** „Anlegen“ ohne jede Rolle ist gesperrt; das Sofort-Popup sagt warum (Hausregel „Sofort-Popup statt title“). */
  const anlegenHinweis = useSofortPopup<HTMLElement>(rolleGewaehlt ? "" : PARTNER_TEXT.atLeastOne);
  /** Läuft gerade „Rolle Kunde hinzufügen“ — der Riegel gegen einen zweiten Klick. */
  const [hinzuLaeuft, setHinzuLaeuft] = useState(false);

  /**
   * „Rolle Kunde hinzufügen“ am Treffer (Spec „Wortwahl“, Dublettenhinweis): Die Firma gibt es schon — statt einer
   * zweiten Firmenzeile bekommt sie die Kundenrolle im aktiven Unternehmen. Besteht dort eine Beziehung (etwa als
   * Lieferant), setzt der Rollenweg die Kundenrolle dazu; sonst entsteht die Beziehung als Kunde. Erst nach der
   * gestapelten Bestätigung, danach in die Akte.
   */
  async function kundeHinzufuegen(t: DublettenTreffer): Promise<void> {
    if (aktiv === undefined || hinzuLaeuft) return;
    if (!(await bestaetigen({ titel: PARTNER_TEXT.confirmAddCustomerRole(t.companyName, aktiv.shortCode),
      bestaetigenText: PARTNER_TEXT.add }))) return;
    setMeldung(null);
    setHinzuLaeuft(true);
    try {
      if (t.relationRoles.some((r) => r.shortCode === aktiv.shortCode)) {
        const akte = await p.client.getCustomer(t.id);
        const r = akte.relations.find((x) => x.companyId === aktiv.id);
        if (r === undefined) throw new Error(PARTNER_TEXT.addCustomerRoleFailed);
        await p.client.setRelationRoles(t.id, r.id, { version: r.version, status: "ACTIVE", supplierStatus: r.supplierStatus });
      } else {
        await p.client.addRelation(t.id, { companyId: aktiv.id, customerNumber: null, status: "ACTIVE",
          supplierStatus: null, customerSince: null });
      }
      zurAkteDesTreffers(t.id);
    } catch (fehler) {
      setMeldung(fehler instanceof Error && fehler.message !== "" ? fehler.message : PARTNER_TEXT.addCustomerRoleFailed);
    } finally {
      setHinzuLaeuft(false);
    }
  }

  /** Die Vorschlagsleiste zu einem Feld: Wert, Quelle und ein Übernehmen-Knopf, der SICHTBAR
   *  und nur dieses eine Feld setzt. Trägt das Feld den Vorschlag schon, steht statt des
   *  Knopfs „übernommen" — nichts wird je still ersetzt. */
  function vorschlagsLeiste(feld: keyof Entwurf, label: string): ReactElement | null {
    const v = vorschlaege.find((s) => s.field === feld);
    if (v === undefined) return null;
    const schonUebernommen = entwurf[feld] === v.value;
    return (
      <div className="ka__vorschlag" role="group" aria-label={`Vorschlag für ${label}`}>
        <span className="ka__vorschlag-wert">{v.value}</span>
        <span className="ka__vorschlag-quelle">{SOURCE_LABEL[v.source]}</span>
        {schonUebernommen
          ? <span className="ka__vorschlag-uebernommen">übernommen</span>
          : (
            <KiKnopf className="ka__vorschlag-knopf"
              onClick={() => setEntwurf((e) => ({ ...e, [feld]: v.value }))}>
              Übernehmen
            </KiKnopf>
          )}
      </div>
    );
  }

  /** Ein Feld samt Vorschlagsleiste als eine Rasterzelle — so sitzt der Vorschlag UNTER dem
   *  Feld, ohne das Tippen zu stören (kein Layout-Sprung, der den Fokus verlöre). */
  function feldMitVorschlag(feld: keyof Entwurf, unten?: ReactElement | null): ReactElement {
    const f = FELDER.find((x) => x.name === feld);
    const fehlerText = feldFehler[feld];
    return (
      <div className="ka__feld-mit-vorschlag" key={feld}>
        <Feld id={`ka-${feld}`} label={f?.label ?? feld} wert={entwurf[feld]} bearbeitbar
          onChange={(wert) => setEntwurf((e) => ({ ...e, [feld]: wert }))}
          {...(f?.typ !== undefined ? { typ: f.typ } : {})}
          {...(fehlerText !== undefined ? { fehler: fehlerText } : {})}
        />
        {vorschlagsLeiste(feld, f?.label ?? feld)}
        {unten}
      </div>
    );
  }

  /** Der VIES-Gültigkeitsbefund — ein Hinweis neben der USt-IdNr, KEIN übernehmbarer Wert. */
  // Der VIES-Gültigkeitsbefund als grüner Haken (Befund Dirk 01.09.): unmissverständlich
  // sichtbar, dass die USt-IdNr laut VIES gültig ist — auch wenn (wie bei deutschen Nummern)
  // KEIN Firmenname mitkommt, weil Deutschland ihn über VIES nicht herausgibt. role="status"
  // trägt den Text als zugängliche Bezeichnung; der Haken selbst ist aria-hidden.
  const viesBefund = vorschlaege.find((s) => s.field === "vatIdValid");
  const viesHinweis = viesBefund === undefined ? null : viesBefund.value === "true" ? (
    <span className="ka__vies-gueltig" role="status" aria-label="laut VIES gültig">
      <span aria-hidden="true">✓</span> laut VIES gültig
    </span>
  ) : (
    <span className="ka__vies-ungueltig" role="status" aria-label="laut VIES ungültig">
      <span aria-hidden="true">✗</span> laut VIES ungültig
    </span>
  );


  const anlegenText = treffer.length > 0 ? PARTNER_TEXT.createAnyway : PARTNER_TEXT.create;

  return (
    <Arbeitsdialog titel={PARTNER_TEXT.newPartner} panel="partner-neu" onAbbrechen={() => { void abbrechen(); }}
      abbrechenGesperrt={arbeitet}
      // Die Meldungen stehen im Slot des Panels und rollen nicht mit dem Inhalt davon.
      meldungen={meldung === null && formular.meldung === null ? undefined : (
        <>
          {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
        </>
      )}
      fuss={(
        <>
          {angelegtId !== null ? (
            // Der Kunde ist angelegt, nur das Logo scheiterte: kein zweites Anlegen (das
            // erzeugte eine Dublette), sondern der ausdrückliche Sprung in die Akte.
            <KiKnopf art="primaer" onClick={() => { fertig(angelegtId); }}>{PARTNER_TEXT.toRecord}</KiKnopf>
          ) : (
            <KiKnopf art="primaer" deaktiviert={arbeitet || !rolleGewaehlt} knopfRef={anlegenHinweis}
              onClick={() => { void anlegen(); }}>
              {anlegenText}
            </KiKnopf>
          )}
          <KiKnopf deaktiviert={arbeitet} onClick={() => { void abbrechen(); }}>{PARTNER_TEXT.cancel}</KiKnopf>
        </>
      )}>
    {/* Eigene Wurzelklasse (NICHT "ka"): Die Kundenakte (KundenAkte.tsx) benutzt ebenfalls
        ".ka", und deren globale Regeln trügen sonst über beide Ansichten (Befund Dirk
        01.09.2026 — die Akte war dadurch gedeckelt, die Tab-Leiste konnte die Breite nie
        nutzen). Seit Entscheid 524 setzen Breite und Rand das Panel. */}
    <div className="ka-neu">
      <p className="ka__anreicherung-hinweis">
        Sobald eine <strong>USt-IdNr.</strong> oder eine <strong>Webseite</strong> eingetragen
        ist, schlägt das System dazu Stammdaten und ein Logo vor (aus VIES, Impressum und der
        Webseite) — ganz ohne Knopf. Die Vorschläge übernimmst du einzeln; nichts wird
        automatisch gesetzt. Der Firmenname allein löst noch keine Suche aus.
      </p>

      {/* Das Formular (me-formular) trägt die Zustände: Sperre während des Anlegens,
          Sprung zum ersten Fehler. Beschriftung „oben": Die Felder bringen ihre eigene
          Beschriftung mit; das Raster je Abschnitt (zwei Spalten ab Tablet) bleibt
          `ka__felder` — jeder Abschnitt ist EINE Zeile des Formulars. */}
      <Formular beschriftung="oben" {...formular.formularProps}>
      <div className="ka-neu__formular">
      {/* Zuerst der Firmenname — er treibt die Dublettensuche. */}
      <div className="ka__felder">
        <div className="ka__feld-mit-vorschlag">
          <Feld id="ka-companyName" label="Firmenname" wert={entwurf.companyName}
            bearbeitbar
            onChange={(wert) => setEntwurf((e) => ({ ...e, companyName: wert }))}
            {...(feldFehler.companyName !== undefined ? { fehler: feldFehler.companyName } : {})}
          />
          {vorschlagsLeiste("companyName", "Firmenname")}
        </div>
      </div>

      {treffer.length > 0 && (
        <section className="ka__dubletten" aria-label="Mögliche Dubletten">
          <h3 className="ka__dubletten-titel">Gibt es diese Firma schon?</h3>
          <p className="ka__dubletten-text">
            Ähnlich benannte Firmen sind bereits erfasst. Gehört eine davon zu einem anderen
            Unternehmen, wechsle dorthin und füge in der Kundenakte unter Stammdaten dein
            Unternehmen hinzu — so entsteht keine zweite Firmenzeile.
          </p>
          <ul className="ka__dubletten-liste">
            {treffer.map((t) => {
              // Die Rollen des Treffers je Kürzel (P12) — Nummern und Status bleiben geheim.
              const rollenText = t.relationRoles.map((r) => PARTNER_TEXT.rolesAt(
                r.roles.length === 0 ? [PARTNER_TEXT.noCurrentRole] : r.roles.map((x) => ROLLEN_TEXT[x]), r.shortCode)).join(", ");
              const hier = aktiv === undefined ? undefined : t.relationRoles.find((r) => r.shortCode === aktiv.shortCode);
              const schonKunde = hier !== undefined && hier.roles.some((x) => x !== "SUPPLIER");
              return (
                <li key={t.id} className="ka__dublette">
                  <span className="ka__dublette-name">{PARTNER_TEXT.duplicate(t.companyName, rollenText)}</span>
                  {ortVon(t) !== "" && (
                    <span className="ka__dublette-ort">{ortVon(t)}</span>
                  )}
                  {/* Absichtlich nur Kürzel und Rollen: Die Suche geht über ALLE Unternehmen und
                      liefert deshalb nur die TATSACHE einer Beziehung und ihre Rollen, keine fremden
                      Nummern, Stati oder Konditionen (siehe Spezifikation, P12). */}
                  <span className="ka__dublette-chips">
                    {t.relationShortCodes.map((kuerzel) => (
                      <Chip key={kuerzel} className="ka__chip"
                        {...hausfarbeChip(p.companies, kuerzel)}>{kuerzel}</Chip>
                    ))}
                  </span>
                  {/* Im aktiven Unternehmen schon Kunde oder Interessent: in seine Akte. Sonst „Rolle Kunde
                      hinzufügen“ — an der vorhandenen Beziehung (etwa als Lieferant) oder als neue Beziehung hier.
                      Gehört die Firma woandershin, bleibt daneben der Wechsel des aktiven Unternehmens (E361, R20).
                      Hat die Maske schon Eingaben, fragt der Verlassen-Wächter — beim Wechsel
                      (wechsleUnternehmen ruft ihn) wie beim Sprung in die Akte (die Navigation
                      ruft ihn); nur mit dem Suchnamen allein geht es in beiden Fällen ohne Frage hinüber. */}
                  {!schonKunde && aktiv !== undefined && (
                    <KiKnopf className="ka__dublette-knopf" deaktiviert={arbeitet || hinzuLaeuft}
                      onClick={() => { void kundeHinzufuegen(t); }}>
                      {PARTNER_TEXT.addCustomerRole}
                    </KiKnopf>
                  )}
                  {hier !== undefined ? (
                    <KiKnopf className="ka__dublette-knopf" deaktiviert={arbeitet}
                      onClick={() => zurAkteDesTreffers(t.id)}>
                      {PARTNER_TEXT.toRecord}
                    </KiKnopf>
                  ) : t.relationShortCodes.map((kuerzel) => {
                    const firma = p.companies.find((c) => c.shortCode === kuerzel);
                    return firma === undefined ? null : (
                      <KiKnopf key={kuerzel} className="ka__dublette-knopf" deaktiviert={arbeitet}
                        onClick={() => p.onWechseln(firma.id, t.id, { ungefragt: !weitereEingaben })}>
                        Zu {kuerzel} wechseln
                      </KiKnopf>
                    );
                  })}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="ka__felder">
        {FELDER.map((f) => {
          // Land, PLZ und Ort stehen in EINER Zeile (Spec „Adresszeile", E400); die
          // Anreicherungs-Vorschläge zu PLZ und Ort stehen darunter. Der Ort steckt in der Zeile.
          // Der `key` bindet den inneren Stand der Zeile an den Stand der Maske: Mit dem Anlegen
          // beginnt ein neuer (M-6a).
          if (f.name === "city") return null;
          if (f.name === "postalCode") {
            return (
              <div className="ka__ortszeile" key="ortszeile">
                <Ortszeile key={angelegtId ?? "neu"} praefix="ka" laender={laender}
                  plzOrte={p.client.listPlzOrte} gesperrt={formular.zustand !== "bereit"}
                  werte={{ country: entwurf.country, postalCode: entwurf.postalCode, city: entwurf.city }}
                  fehler={feldFehler}
                  onAenderung={(teil) => setEntwurf((e) => ({ ...e, ...teil }))} />
                {vorschlagsLeiste("postalCode", "PLZ")}
                {vorschlagsLeiste("city", "Ort")}
              </div>
            );
          }
          // industry trägt keinen Vorschlag; der VIES-Befund hängt an der USt-IdNr.
          return feldMitVorschlag(f.name, f.name === "vatId" ? viesHinweis : null);
        })}
      </div>

      {/* Die Logo-Kacheln samt Markenrechts-Dialog — seit Entscheid 673 gemeinsam mit den Stammdaten. */}
      <LogoChoice name="ka-logo" candidates={logoKandidaten} chosen={gewaehltesLogo}
        onChosen={setGewaehltesLogo} help={LOGO_TEXT.helpCreate} />

      <h3 className="ka__zwischentitel">{PARTNER_TEXT.relationAt(aktiv?.name ?? PARTNER_TEXT.empty)}</h3>
      <div className="ka__felder">
        {/* Die Rollenwahl (C9, P2): Vorgabe aus den Chips der Liste, mindestens eine. */}
        <RoleChoiceField wert={rollen} onWert={setRollen}
          fehler={feldFehler.status ?? feldFehler.supplierStatus} />

        {mitKundenrolle && (
          <Feld id="ka-customerNumber" label="Kundennummer" wert={nummer} bearbeitbar
            onChange={setNummer} fehler={feldFehler.customerNumber}
            hilfe={"Leer lassen — die Nummer wird beim Speichern vergeben. Eine bestehende "
              + "Nummer (z. B. aus dem Portal) trägst du hier ein."} />
        )}

        {mitKundenrolle && (
          <Feld id="ka-customerSince" label="Kunde seit" wert={seit} bearbeitbar typ="date"
            onChange={setSeit}
            {...(feldFehler.customerSince !== undefined
              ? { fehler: feldFehler.customerSince } : {})}
          />
        )}
      </div>
      </div>
      </Formular>
    </div>
    </Arbeitsdialog>
  );
}
